// core/db/db.ts

import { Mutex } from 'async-mutex'

import { IDatabasePresenter } from '@makyoapp/makyo-sdk/models/Database'
import { ColumnTypes, ITableRow, ITableSnapshotSettings } from '@makyoapp/makyo-sdk/models/Table'

import DbWorker from '@/workers/db-worker?worker'
import { DbWorkerClient } from '@/workers/types'

import DataWorker from './operations/data-worker?worker'
import { IndexedDBManager } from './persisted/idb'
import { IDBProvider } from './provider'
import { DBTable, DEFAULT_LANGUAGE, IDBTable } from './table'
import { getActiveLanguage } from './utils'

const mutex = new Mutex()

// errors
const dbErrors = {
  DB_NOT_OPENED: 'ERR_DB_NOT_OPENED',
  TABLE_NOT_FOUND: 'ERR_TABLE_NOT_FOUND',
}

type Subscriber = () => void

export type GroupedTable = {
  id: string
  name: string
  type: 'user' | 'session' | 'query' | 'snapshot' | 'source'
  source?: string
  parent?: string
  childrenCount?: number
}

/**
 * Represents a database interface.
 */
export interface IDB {
  /**
   * Current Db Worker
   */
  dbWorker: DbWorkerClient
  /**
   * Data Worker
   */
  dataWorker: Worker
  /**
   * Current Database Settings
   */
  settings?: IDatabasePresenter

  /**
   * Current Active Database ID opened
   */
  dbID?: string

  /**
   * IndexedDBManager instance
   */
  indexedDb: IndexedDBManager

  /**
   * Current Db Tables Map
   */
  tablesMap: Map<string, IDBTable>

  /**
   * Fetched table ids, managed by dbWorker (wasm)
   */
  fetchedTableIds: Set<string>

  /**
   * Current Grouped Tables Array
   */
  groupedTables: GroupedTable[]

  /**
   * Set Tables Map
   * @param {Map<string, IDBTable>} tablesMap - The tables map to set.
   */
  SetTablesMap: (tablesMap: Map<string, IDBTable>) => void

  /**
   * All tables snapshot reference map
   */
  snapshotRefMap: Map<string, Set<string>>

  /**
   * All tables query reference map
   */
  queryRefMap: Map<string, Set<string>>

  /**
   * Current opened state
   */
  opened: boolean

  /**
   * dbClient instance subscriber callback
   */
  subscribe(callback: Subscriber): () => void

  /**
   * Notify subscribers - Notify all subscribers.
   */
  notifySubscribers: () => void

  /**
   * PullTables - Fetches the tables from the database and updates the list of tables.
   * It should be called after Open.
   *
   * @returns {Promise<void>} - A promise that resolves when the tables are fetched.
   */
  PullTables: () => Promise<void>

  /**
   * GetTables - Returns the list of tables in the database.
   * @returns {[]IDBTable} - A map of tables in the database. // The mast be ordered by the index property.
   */
  GetTables: () => IDBTable[]

  /**
   * BuildGroupedTables - Returns the list of tables in the database, grouped by source table.
   * @returns {GroupedTable[]} - A map of tables in the database.
   */
  BuildGroupedTables: () => void

  /**
   * Rename - Renames the database to the given name.
   *
   * @param {string} name - The new name for the database.
   * @returns {Promise<void>} - A promise that resolves when the rename operation is complete.
   */
  Rename: (name: string) => Promise<void>

  /**
   * Delete - Deletes the database.
   *
   * @returns {Promise<void>} - A promise that resolves when the delete operation is complete.
   */
  Delete: () => Promise<void>

  /**
   * CreateTable - Cretae the table with the given name.
   * @param {string} tableName - The name of the table to create.
   */
  CreateTable: (tableName: string, type?: 'default' | 'import') => Promise<IDBTable>

  /**
   * DeleteTable - Deletes the table with the given id.
   * @param {string} tableID - The ID of the table to delete.
   * @returns {Promise<string>} - The ID of the next table to redirect to.
   */
  DeleteTable: (tableID: string) => Promise<string | undefined>

  /**
   * ReorderTable - Reorders the table with the given id.
   * @param {string} tableId - The ID of the table to reorder.
   * @param {number} newIndex - The new index of the table.
   * @param {number} oldIndex - The old index of the table.
   */
  ReorderTable: (tableId: string, newIndex: number, oldIndex: number) => Promise<void>

  /**
   * CreateSnapshotOrQueryTable - Creates a snapshot table with the given name and settings.
   * @param {string} tableName - The name of the table to create.
   * @param {ITableSnapshotSettings} settings - The settings for the table.
   */
  CreateSnapshotOrQueryTable: (
    type: 'snapshot' | 'query',
    tableName: string,
    settings: ITableSnapshotSettings
  ) => Promise<IDBTable>

  /**
   * UpdateLanguages - Updates the languages of the database.
   * @param {string[]} languages - The languages to update.
   * @param {string} defaultLanguage - The default language to update.
   */
  UpdateLanguages: (languages: string[], defaultLanguage?: string) => Promise<void>

  /**
   * SetDbWorker - Sets the dbWorker.
   * @param {DbWorkerClient} dbWorker - The dbWorker to set.
   */
  SetDbWorker: (dbWorker: DbWorkerClient) => void

  /**
   * Close - Closes the connection to the database and releases resources. It should be called when the database is no longer needed.
   * @returns {void}
   */
  Close: () => void
}

//--------------------------------------------------------------------------------------//
//                               DB Client Implementation                               //
//--------------------------------------------------------------------------------------//

const dbClientsMap: Map<string, DBClient> = new Map()

export class DBClient implements IDB {
  provider: IDBProvider
  settings?: IDatabasePresenter
  dbID: string
  opened = false
  subscribers: Set<Subscriber> = new Set()

  tablesMap: Map<string, IDBTable> = new Map()
  snapshotRefMap: Map<string, Set<string>> = new Map()
  queryRefMap: Map<string, Set<string>> = new Map()

  dbWorker = new DbWorker() as DbWorkerClient
  dataWorker = new DataWorker()
  indexedDb = new IndexedDBManager()

  fetchedTableIds: Set<string> = new Set<string>()
  tableIds: string[] = [] // used for tables re-ordering
  groupedTables: GroupedTable[] = []

  private constructor(dbID: string, provider: IDBProvider) {
    this.dbID = dbID
    this.provider = provider
  }

  subscribe(callback: Subscriber): () => void {
    this.subscribers.add(callback)
    return () => {
      this.subscribers.delete(callback)
    }
  }

  notifySubscribers(): void {
    this.subscribers.forEach((callback) => {
      try {
        callback()
      } catch (error) {
        console.error('Error in db subscriber:', error)
      }
    })
  }

  public static async Open(dbID: string, provider: IDBProvider): Promise<DBClient> {
    return await mutex.runExclusive(async () => {
      if (dbClientsMap.has(dbID)) {
        let existingClient = dbClientsMap.get(dbID)
        if (existingClient) return existingClient
      }

      const newClient = new DBClient(dbID, provider)
      dbClientsMap.set(dbID, newClient)

      // Check if settings and tablesMap are defined. If yes return void without fetching the data.
      if (newClient.settings && newClient.tablesMap.size > 0) {
        return newClient
      }

      // Fetch the database settings.
      const db = await newClient.provider.getDB(newClient.dbID)

      if (db && db._id) {
        newClient.settings = db
        await newClient.PullTables()
        newClient.opened = true
        return newClient
      } else {
        throw new Error(dbErrors.DB_NOT_OPENED)
      }
    })
  }

  SetTablesMap(tablesMap: Map<string, IDBTable>) {
    this.tablesMap = tablesMap
  }

  async PullTables(): Promise<void> {
    if (!this.settings) {
      throw new Error(dbErrors.DB_NOT_OPENED)
    }

    try {
      const tables = await this.provider.listTables(this.settings._id)

      // Set ordered table ids
      this.tableIds = tables.map((table) => table._id)

      // Create DBTable objects and set tablesMap in a single pass
      const path = window.location.pathname
      const pathSegments = path.split('/') // Split the path into segments

      const projectId = pathSegments[2]
      const dbLanguage = getActiveLanguage(projectId) || this.settings?._DefaultLanguage || DEFAULT_LANGUAGE

      this.tablesMap = new Map(
        tables.map((table, index) => {
          const dbTable = new DBTable(this, this.provider, dbLanguage, index, table)
          return [table._id, dbTable]
        })
      )

      // Set the tablesMap for each table
      this.tablesMap.forEach((table) => {
        table.setTablesMap(this.tablesMap)
      })

      // Setup snapshot/query tables
      await Promise.all(
        Array.from(this.tablesMap.values())
          .filter((table) => table.isSnapshot || table.isQuery)
          .map((table) => table.setupSnapshotQueryTable())
      )

      // Setup grouped tables (app builder only)
      this.BuildGroupedTables()
      //
    } catch (err) {
      console.error('Error in PullTables:', err)
      throw err
    }
  }

  GetTables(): IDBTable[] {
    // Check if tablesMap is defined.
    if (this.tablesMap.size === 0) {
      return []
    }

    // Optimize: use sorted tableIds to map the tables
    return this.tableIds.map((tableId) => this.tablesMap.get(tableId)).filter((table) => table !== undefined)
  }

  BuildGroupedTables() {
    // only allowed in app builder
    if (window.appMode !== 'preview') {
      return
    }

    // sanity check
    if (this.tableIds.length === 0) {
      return
    }

    const getTableType = (table: IDBTable): GroupedTable['type'] => {
      if (table.settings._IsUserTable) return 'user'
      if (table.isSessionTable) return 'session'
      if (table.isQuery) return 'query'
      if (table.isSnapshot) return 'snapshot'
      return 'source'
    }

    const sourceTables: IDBTable[] = []
    const sourceToDerived = new Map<string, IDBTable[]>()

    // Single pass to categorize all tables
    for (const tableId of this.tableIds) {
      const table = this.tablesMap.get(tableId)
      if (!table) {
        continue
      }

      if (table.isQuery || table.isSnapshot) {
        const sourceTable = table.GetSnapshotSourceTable()
        if (sourceTable) {
          const sourceId = sourceTable.id
          if (!sourceToDerived.has(sourceId)) {
            sourceToDerived.set(sourceId, [])
          }
          sourceToDerived.get(sourceId)!.push(table)
        }
      } else {
        sourceTables.push(table)
      }
    }

    const groupTablesArray: GroupedTable[] = []

    // Process standard tables (source tables + userTable + sessionTable)
    for (const table of sourceTables) {
      const derived = sourceToDerived.get(table.id)
      const groupedSource: GroupedTable = {
        id: table.id,
        name: table.settings._Name,
        type: getTableType(table),
        childrenCount: derived ? derived.length : 0,
      }
      groupTablesArray.push(groupedSource)

      if (derived) {
        for (const childTable of derived) {
          const groupedChild: GroupedTable = {
            id: childTable.id,
            name: childTable.settings._Name,
            type: getTableType(childTable),
            source: table.id,
            parent: table.id,
          }
          groupTablesArray.push(groupedChild)
        }
      }
    }

    this.groupedTables = groupTablesArray
  }

  async Rename(newDatabaseName: string): Promise<void> {
    // use sdk to rename the database
    if (!this.settings?._id) {
      throw new Error(dbErrors.DB_NOT_OPENED)
    }
    await this.provider.renameDB(this.settings?._id, newDatabaseName)
    this.notifySubscribers()
  }

  async Delete(): Promise<void> {
    // use sdk to delete the database
    if (!this.settings?._id) {
      throw new Error(dbErrors.DB_NOT_OPENED)
    }
    await this.provider.deleteDB(this.settings?._id)
    this.Close()
  }

  async CreateTable(tableName: string, type: 'default' | 'import' = 'default'): Promise<IDBTable> {
    if (!this.settings?._id) {
      throw new Error(dbErrors.DB_NOT_OPENED)
    }

    const response = await this.provider.createTable(tableName, this.dbID)

    // set into tablesMap
    const newTable = new DBTable(this, this.provider, this.settings._DefaultLanguage, this.tablesMap.size, response)
    this.tablesMap.set(response._id, newTable)
    newTable.setTablesMap(this.tablesMap)

    if (type === 'default') {
      // create new column (text)
      let resNewTextColumn = await this.provider.addColumn(
        newTable.id,
        {
          _id: '',
          _Name: 'Text',
          _Type: ColumnTypes.Text,
          _Settings: {},
          _Connection: {
            _ForeignColumnIDToShow: '',
            _ForeignColumnID: '',
            _ForeignTableID: '',
            _LocalColumnID: '',
            _MatchMultiple: false,
          },
          _Lookup: {
            _ForeignColumnIDToShow: '',
            _ConnectionColumnID: '',
            _Standard: false,
          },
        },
        this.dbID
      )

      // add slug column
      let resNewSlugColumn = await this.provider.addColumn(
        newTable.id,
        {
          _id: '',
          _Name: 'slug',
          _Type: ColumnTypes.Slug,
          _Settings: {
            slugColumn: resNewTextColumn._id,
          },
          _Connection: {
            _LocalColumnID: '',
            _ForeignTableID: '',
            _ForeignColumnID: '',
            _ForeignColumnIDToShow: '',
            _MatchMultiple: false,
          },
          _Lookup: {
            _ForeignColumnIDToShow: '',
            _ConnectionColumnID: '',
            _Standard: false,
          },
        },
        this.dbID
      )

      newTable.setTableColumns([resNewTextColumn, resNewSlugColumn])

      // update dbWorker
      this.dbWorker.sendAction({
        type: 'set_tables_infos',
        payload: [newTable.settings],
      })

      // hide slug column by default
      await newTable.HideColumn(resNewSlugColumn._id, true, false)

      // add initial row
      newTable.AddRow({
        [resNewTextColumn._id]: '',
      } as ITableRow)
    }

    // update grouped tables
    this.tableIds.push(newTable.id)
    this.BuildGroupedTables()
    this.notifySubscribers()

    return newTable
  }

  async CreateSnapshotOrQueryTable(
    type: 'snapshot' | 'query',
    tableName: string,
    settings: ITableSnapshotSettings,
    timezone?: string
  ): Promise<IDBTable> {
    if (!this.settings) {
      throw new Error(dbErrors.DB_NOT_OPENED)
    }

    const newTableResponse =
      type === 'snapshot'
        ? await this.provider.createSnapshotTable(tableName, this.dbID, settings)
        : await this.provider.createQueryTable(tableName, this.dbID, settings)

    const newTable = new DBTable(
      this,
      this.provider,
      this.settings._DefaultLanguage,
      this.tablesMap.size,
      newTableResponse
    )

    if (timezone) {
      await newTable.UpdateTimezone(timezone)
    }

    this.tablesMap.set(newTableResponse._id, newTable)
    newTable.setTablesMap(this.tablesMap)
    await newTable.setupSnapshotQueryTable()

    // update grouped tables
    this.tableIds.push(newTableResponse._id)
    this.BuildGroupedTables()

    // update dbWorker with new table
    this.dbWorker.sendAction({
      type: 'set_tables_infos',
      payload: [newTable.settings],
    })

    this.notifySubscribers()

    return newTable
  }

  async DuplicateTable(type: 'snapshot' | 'query', tableID: string): Promise<IDBTable> {
    if (!this.settings) throw new Error(dbErrors.DB_NOT_OPENED)

    const tableToCopy = this.tablesMap.get(tableID)
    if (!tableToCopy) throw new Error(dbErrors.TABLE_NOT_FOUND)

    const tableName = `${tableToCopy.settings._Name} copy`
    const natureSettings = tableToCopy.settings._NatureSettings as ITableSnapshotSettings

    const response =
      type === 'query'
        ? await this.provider.duplicateQueryTable(tableName, tableID, this.dbID)
        : await this.provider.createSnapshotTable(tableName, this.dbID, natureSettings)

    // set into tablesMap
    const newTable = new DBTable(this, this.provider, this.settings._DefaultLanguage, this.tablesMap.size, response)
    this.tablesMap.set(response._id, newTable)
    newTable.setTablesMap(this.tablesMap)

    // update grouped tables
    this.tableIds.push(newTable.id)
    this.BuildGroupedTables()

    return newTable
  }

  async DeleteTable(tableID: string): Promise<string | undefined> {
    // use sdk to delete the table
    if (!this.settings?._id) {
      throw new Error(dbErrors.DB_NOT_OPENED)
    }
    if (!tableID) {
      throw new Error(dbErrors.TABLE_NOT_FOUND)
    }
    await this.provider.deleteTable(tableID, this.settings?._id)

    // update tablesMap
    this.tablesMap.delete(tableID)

    // update grouped tables
    this.tableIds = this.tableIds.filter((id) => id !== tableID)
    this.BuildGroupedTables()

    this.notifySubscribers()

    // find the next table id
    const nextTableId = this.tableIds[this.tableIds.indexOf(tableID) + 1]
    return nextTableId
  }

  async ReorderTable(tableId: string, newIndex: number, oldIndex: number) {
    if (!this.settings) return

    const movingItem = this.groupedTables[oldIndex]

    // sanity check
    if (!movingItem || movingItem.id !== tableId) {
      console.error('ReorderTable: tableId and oldIndex mismatch.')
      return
    }

    // find the item and its children to move together
    const itemsToMove: GroupedTable[] = [movingItem]
    if (movingItem.type === 'source') {
      let currentIndex = oldIndex + 1
      while (currentIndex < this.groupedTables.length && this.groupedTables[currentIndex].parent === movingItem.id) {
        itemsToMove.push(this.groupedTables[currentIndex])
        currentIndex++
      }
    }

    // perform reordering
    const newGroupTablesArray = [...this.groupedTables]
    const movedItems = newGroupTablesArray.splice(oldIndex, itemsToMove.length)
    newGroupTablesArray.splice(newIndex, 0, ...movedItems)

    // new validation logic: prevent moving children to different parents
    if (movingItem.parent) {
      let newParentId: string | undefined
      // find the new parent by looking for the first source table above the new index
      for (let i = newIndex - 1; i >= 0; i--) {
        if (newGroupTablesArray[i].type === 'source') {
          newParentId = newGroupTablesArray[i].id
          break
        }
      }

      if (movingItem.parent !== newParentId) {
        console.warn('A child table cannot be moved to a different source table.')
        return // Abort the reorder
      }
    }

    // update tableIds with the new order
    this.groupedTables = newGroupTablesArray

    // update tableIds
    this.tableIds = newGroupTablesArray.map((t) => t.id)

    // persist the new order
    this.provider.reorderTable(this.dbID, this.tableIds)

    this.notifySubscribers()
  }

  async UpdateLanguages(languages: string[], defaultLanguage?: string) {
    if (!this.settings) return

    this.settings._Languages = languages
    if (defaultLanguage) {
      this.settings._DefaultLanguage = defaultLanguage
    }

    this.notifySubscribers()

    await this.provider.updateDb(this.dbID, {
      _Languages: languages,
      _DefaultLanguage: defaultLanguage ?? this.settings._DefaultLanguage ?? '',
    })
  }

  SetDbWorker(dbWorker: DbWorkerClient) {
    // terminate the old dbWorker
    this.dbWorker.terminate()

    // set the new dbWorker
    this.dbWorker = dbWorker
  }

  Close(): void {
    dbClientsMap.delete(this.dbID)
    this.settings = undefined
    this.opened = false
    this.tablesMap.clear()
    this.fetchedTableIds.clear()
    this.groupedTables = []
    this.subscribers.clear()
    this.dbWorker.terminate()
    this.dataWorker.terminate()
    this.indexedDb.close()
    this.snapshotRefMap.clear()
    this.queryRefMap.clear()
  }
}
